import type { FormEvent } from 'react';
import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import useBusinessUsers from '../../hooks/useBusinessUsers';
import { BUSINESS_USER_ROLES, BUSINESS_USER_STATUSES, formatEnumLabel } from '../../services/businessConstants';
import '../../styles/BusinessUsersPage.css';

interface BusinessUsersPageProps {
  businessEntityId: string;
}

const BusinessUsersPage = ({ businessEntityId }: BusinessUsersPageProps) => {
  const { user: currentUser } = useAuth();
  const { users, loading: usersLoading, list, add, update, remove } = useBusinessUsers(businessEntityId);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    list({ page: 0, size: 50 });
  }, [businessEntityId, list]);

  const handleAddUser = () => {
    setEditingUser(null);
    setShowForm(true);
  };

  const handleEditUser = (user: any) => {
    setEditingUser(user);
    setShowForm(true);
  };

  const handleSaveUser = async (userData: any) => {
    if (!currentUser?.id) {
      setMessage({ type: 'error', text: 'You must be signed in to save a business user.' });
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      if (editingUser?.id) {
        await update(editingUser.userId || editingUser.id, {
          role: userData.role,
          status: userData.status,
          primaryContact: userData.primaryContact,
        }, currentUser.id);
        setMessage({ type: 'success', text: 'Business user updated.' });
      } else {
        await add(
          {
            email: userData.email,
            role: userData.role,
            status: userData.status,
            primaryContact: userData.primaryContact,
          },
          currentUser.id
        );
        setMessage({ type: 'success', text: 'Business user added.' });
      }
      setShowForm(false);
      setEditingUser(null);
      await list({ page: 0, size: 50 });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Failed to save business user.' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveUser = async (record: any) => {
    if (!currentUser?.id || !window.confirm('Remove user from business?')) return;

    setSaving(true);
    setMessage(null);
    try {
      await remove(record.userId || record.id, currentUser.id);
      setMessage({ type: 'success', text: 'Business user removed.' });
      await list({ page: 0, size: 50 });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Failed to remove business user.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="business-users-page">
      {message && <div className={`user-message ${message.type}`}>{message.text}</div>}

      <div className="users-header">
        <h2>Business Users</h2>
        <button className="btn-add-user" onClick={handleAddUser}>
          + Add User
        </button>
      </div>

      {showForm ? (
        <div className="user-form-container">
          <button
            className="btn-close-form"
            onClick={() => {
              setShowForm(false);
              setEditingUser(null);
            }}
          >
            Back
          </button>
          <UserForm initialValues={editingUser} onSubmit={handleSaveUser} loading={saving} />
        </div>
      ) : (
        <div className="users-list">
          {usersLoading ? (
            <p className="no-users">Loading users...</p>
          ) : users.length === 0 ? (
            <p className="no-users">No users assigned to this business.</p>
          ) : (
            <div className="users-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Primary</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((businessUser) => {
                    const name = [businessUser.firstName || businessUser.user?.firstName, businessUser.lastName || businessUser.user?.lastName]
                      .filter(Boolean)
                      .join(' ');
                    return (
                      <tr key={businessUser.id || businessUser.userId}>
                        <td>{name || '-'}</td>
                        <td>{businessUser.email || businessUser.user?.email || '-'}</td>
                        <td><span className="role-badge">{formatEnumLabel(businessUser.role || '') || '-'}</span></td>
                        <td>{formatEnumLabel(businessUser.status || '') || '-'}</td>
                        <td>{businessUser.primaryContact ? 'Yes' : 'No'}</td>
                        <td>
                          <div className="action-buttons">
                            <button className="btn-edit" onClick={() => handleEditUser(businessUser)} disabled={saving}>Edit</button>
                            <button className="btn-remove" onClick={() => handleRemoveUser(businessUser)} disabled={saving}>Remove</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface UserFormProps {
  initialValues?: any;
  onSubmit: (data: any) => Promise<void>;
  loading: boolean;
}

const UserForm = ({ initialValues, onSubmit, loading }: UserFormProps) => {
  const [formData, setFormData] = useState({
    email: '',
    role: '',
    status: 'ACTIVE',
    primaryContact: false,
  });
  const [formMessage, setFormMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!initialValues) {
      setFormData({ email: '', role: '', status: 'ACTIVE', primaryContact: false });
      return;
    }

    setFormData({
      email: initialValues.email || initialValues.user?.email || '',
      role: initialValues.role || '',
      status: initialValues.status || 'ACTIVE',
      primaryContact: Boolean(initialValues.primaryContact),
    });
  }, [initialValues]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFormMessage(null);

    if (!formData.email.trim()) {
      setFormMessage({ type: 'error', text: 'User email is required.' });
      return;
    }

    if (!formData.role) {
      setFormMessage({ type: 'error', text: 'Role is required.' });
      return;
    }

    await onSubmit(formData);
  };

  return (
    <form className="user-form" onSubmit={handleSubmit}>
      {formMessage && <div className={`form-message ${formMessage.type}`}>{formMessage.text}</div>}

      <div className="form-group">
        <label>User Email *</label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
          placeholder="user@domain.com"
          disabled={Boolean(initialValues?.id)}
          required
        />
      </div>

      <div className="form-group">
        <label>Role *</label>
        <select value={formData.role} onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value }))} required>
          <option value="">Select role</option>
          {BUSINESS_USER_ROLES.map((role) => (
            <option key={role} value={role}>{formatEnumLabel(role)}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Status</label>
        <select value={formData.status} onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}>
          {BUSINESS_USER_STATUSES.map((status) => (
            <option key={status} value={status}>{formatEnumLabel(status)}</option>
          ))}
        </select>
      </div>

      <div className="form-group checkbox">
        <label>
          <input
            type="checkbox"
            checked={formData.primaryContact}
            onChange={(e) => setFormData((prev) => ({ ...prev, primaryContact: e.target.checked }))}
          />
          Primary Contact
        </label>
      </div>

      <button type="submit" className="btn-save" disabled={loading}>
        {loading ? 'Saving...' : 'Save User'}
      </button>
    </form>
  );
};

export default BusinessUsersPage;
